import {ApiError, useApiMutation, useApiQuery} from "../../../api/useApi";
import {MutationOptions, useQueryClient} from "react-query";
import {BrandingButton} from "./useBrandingButton";
import {BrandingFont} from "./useBrandingFonts";
import {BrandingLogo} from "./useBrandingLogo";
import {useEffect} from "react";
import {injectFontImport} from "../../../helpers/injectFontImport";


// #################################################
//                      TYPES
// #################################################
/**
 * Branding
 * @typedef {Object} Branding
 */
export type Branding = {
    id: number;
    facility_id: number;
    name: string;
    is_active: boolean;
    facility_country_code?: string;
    brandingColors?: BrandingColor[];
    brandingFonts?: BrandingFont[];
    brandingButtons?: BrandingButton[];
    brandingLogos?: BrandingLogo[];
    logo_path?: string | null;
    /** Site d'où la charte a été importée (null si jamais importée). */
    imported_from_url?: string | null;
    imported_at?: string | null;
}

/**
 * Branding Color
 * @typedef {Object} BrandingColor
 */
export type BrandingColor = {
    id: number | null;
    branding_id: number;
    name: string;
    color1 : string;
    color2? : string;
    color_type? : string;
    is_default : boolean;
    slug?: string;
    is_locked?: boolean;
}

// #################################################
//                      QUERIES
// #################################################

/**
 * Hook to get all brandings of the facility
 * @returns Branding[]
 */
export function useBrandings() {
    const url = `/branding`;
    return useApiQuery<Branding[]>(url);
}

/**
 * Hook to get a branding
 * @returns Branding
 */
export function useBranding(branding_id:number|string|undefined) {
    const url = branding_id ? `/branding/${branding_id}` : '';
    return useApiQuery<Branding>(url, {}, {
        enabled: !!branding_id
    });
}

/**
 * Hook to get the active branding of the current facility
 * @returns Branding
 */
export function useActiveBranding() {
    return useApiQuery<Branding>('/branding-current');
}

/**
 * Hook to get the active branding of a facility
 * @returns Branding
 */
export function useFacilityBranding(facility_id:number|string|undefined, options?: {enabled?: boolean}) {
    const url = facility_id ? `/branding-facility?facility_id=${facility_id}` : '';
    return useApiQuery<Branding>(url, {}, {
        enabled: options?.enabled ?? !!facility_id,
    });
}

/**
 * Hook to get the active branding of a loyalty program
 * @returns Branding
 */
export function useLoyaltyBranding(loyalty_program_id:number|string|undefined) {
    const url = loyalty_program_id ? `/branding-loyalty?loyalty_program_id=${loyalty_program_id}` : '';
    return useApiQuery<Branding>(url, {}, {
        enabled: !!loyalty_program_id
    });
}


/**
 * Charte publique par identifiant.
 *
 * Sert aux pages guests qui doivent afficher UNE charte précise — celle que le
 * backend a retenue — et non « la charte active », qui peut être une autre.
 */
export function usePublicBranding(branding_id?: number | null) {
    const url = branding_id ? `/public-branding/${branding_id}` : "";
    return useApiQuery<Branding | null>(url, {}, { enabled: !!branding_id });
}

export function usePublicFacilityBranding(facility_id?: number | string) {
    const url = facility_id ? `/branding-facility?facility_id=${facility_id}` : "";
    return useApiQuery<Branding | null>(
        url,
        {},
        {
            enabled: !!facility_id,
            // si l'API renvoie 204 (no content), axiosClient renverra undefined → on normalise en null
            select: (res) => (res ? res : null),
            retry: false,
        }
    );
}


/**
 * Hook to create a branding
 * @param {MutationOptions<Branding, ApiError, Branding>} options
 */
export function useCreateBranding(options: MutationOptions<Branding, ApiError, Branding> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Branding, ApiError, Branding>(
        'post',
        () => '/branding',
        {

            ...options,

            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/branding'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: true // Refetch inactive queries to ensure data is up-to-date on all pages
                });
                options.onSuccess?.(data, variables, context);
            },

            onError: (error, variables, context) => {
                console.error("An error occurred", error);
                options.onError?.(error, variables, context);
            },

        }
    );
}

/**
 * Hook to delete a branding
 * @returns Branding
 */
export function Branding() {
    const queryClient = useQueryClient();

    return useApiMutation<Branding, ApiError, { id: number }>(
        'delete',
        ({ id }) => `/branding/${id}`,
        {
            // Ajoutez la logique onSuccess ici si nécessaire
            onSuccess: () => {
                // Invalidate all queries that start with '/branding'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/branding'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: true // Refetch inactive queries to ensure data is up-to-date on all pages
                });
            },
        }
    );
}

/**
 * Hook to update a branding
 * @param {MutationOptions<Branding, ApiError, Branding>} options
 */
export function useUpdateBranding(options: MutationOptions<Branding, ApiError, Branding> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Branding, ApiError, Branding>(
        'put',
        (variables) => `/branding/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/branding/${variables.id}`);
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/branding'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: true // Refetch inactive queries to ensure data is up-to-date on all pages
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the branding", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}

/**
 * Hook to duplicate a branding
 */
export function useDuplicateBranding(options: MutationOptions<Branding, ApiError, { id: number }> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Branding, ApiError, { id: number }>(
        'post',
        ({ id }) => `/branding/${id}/duplicate`,
        {
            ...options,
            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/branding'),
                    refetchActive: true,
                    refetchInactive: true,
                });
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while duplicating the branding", error);
                options.onError?.(error, variables, context);
            },
        }
    );
}

export function useEnsureBrandingFonts(targetDoc: Document | null, branding?: Branding) {
    useEffect(() => {
        if (!targetDoc) return;

        // Ajouter preconnect pour les domaines de polices (améliore le temps de chargement)
        const preconnectDomains = [
            'https://fonts.googleapis.com',
            'https://fonts.gstatic.com'
        ];

        // Ajouter le domaine API si on détecte des polices personnalisées
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
        if (apiBaseUrl) {
            preconnectDomains.push(apiBaseUrl);
        }

        preconnectDomains.forEach(domain => {
            const existing = targetDoc.querySelector(`link[rel="preconnect"][href="${domain}"]`);
            if (!existing) {
                const link = targetDoc.createElement('link');
                link.rel = 'preconnect';
                link.href = domain;
                link.crossOrigin = 'anonymous';
                targetDoc.head.appendChild(link);
            }
        });

        if (!branding?.brandingFonts?.length) {
            console.log('[useEnsureBrandingFonts] Pas de fonts à injecter:', {
                hasDoc: !!targetDoc,
                fontsCount: branding?.brandingFonts?.length ?? 0
            });
            return;
        }

        console.log('[useEnsureBrandingFonts] Injection de', branding.brandingFonts.length, 'polices');

        // déduplique par fontFamily.id si dispo
        const seen = new Set<number | string>();
        let injectedCount = 0;

        branding.brandingFonts.forEach((bf: any) => {
            const ff = bf?.fontFamily; // { id, css, import }

            // Déterminer l'import CSS à utiliser
            let fontImport = ff?.import;

            // Fallback: générer l'import Google Fonts si le champ import est vide mais le nom existe
            if (!fontImport && ff?.name) {
                // Encoder le nom de la police pour l'URL Google Fonts
                const encodedName = ff.name.replace(/ /g, '+');
                fontImport = `@import url('https://fonts.googleapis.com/css2?family=${encodedName}:wght@100;200;300;400;500;600;700;800;900&display=swap');`;
                console.log('[useEnsureBrandingFonts] Fallback Google Fonts généré pour:', ff.name);
            }

            if (!fontImport) {
                console.warn('[useEnsureBrandingFonts] Police sans import CSS et sans nom:', {
                    fontFamilyId: ff?.id,
                    fontFamilyName: ff?.name,
                    css: ff?.css
                });
                return;
            }

            const key = `ff-${ff.id ?? ff.css ?? Math.random()}`;
            if (seen.has(key)) {
                console.log('[useEnsureBrandingFonts] Police déjà injectée:', ff.name);
                return;
            }
            seen.add(key);

            // Ajouter un preload pour les fichiers de police (améliore le chargement)
            const urlMatch = fontImport.match(/url\(['"]?([^'")\s]+)['"]?\)/);
            if (urlMatch && urlMatch[1]) {
                const fontUrl = urlMatch[1];
                // Ne pas précharger les CSS Google Fonts (ils contiennent des @font-face, pas des polices)
                if (!fontUrl.includes('fonts.googleapis.com')) {
                    const existingPreload = targetDoc.querySelector(`link[rel="preload"][href="${fontUrl}"]`);
                    if (!existingPreload) {
                        const preloadLink = targetDoc.createElement('link');
                        preloadLink.rel = 'preload';
                        preloadLink.href = fontUrl;
                        preloadLink.as = 'font';
                        preloadLink.crossOrigin = 'anonymous';
                        targetDoc.head.appendChild(preloadLink);
                        console.log('[useEnsureBrandingFonts] Preload ajouté pour:', fontUrl);
                    }
                }
            }

            console.log('[useEnsureBrandingFonts] Injection police:', {
                name: ff.name,
                css: ff.css,
                importLength: fontImport?.length,
                isFallback: !ff?.import
            });
            injectFontImport(targetDoc, key, fontImport);
            injectedCount++;
        });

        console.log('[useEnsureBrandingFonts] Total injecté:', injectedCount);
    }, [targetDoc, branding]);
}
